import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import "./styles/FetchErrorState.css";

interface FetchErrorStateProps {
  /** Human-facing message — already extracted by ApiErrorHelper/thunk rejection. */
  message: string;
  onRetry: () => void;
  retryLabel?: string;
  /** Fires onRetry once automatically after this many seconds (e.g. 30 for a
   *  transient gateway outage). Omit to disable — the manual button still works. */
  autoRetrySeconds?: number;
}

/** Shared "the fetch failed" screen — icon + message + Retry button, with an
 *  optional one-shot auto-retry countdown. Meant to replace empty-state UI
 *  (e.g. "No products found") when the real cause is a failed request, not
 *  an actually-empty list. See ApiErrorHelper.throwIfNotOk, which is what
 *  puts a message like this into a slice's errorMap in the first place. */
const FetchErrorState: React.FC<FetchErrorStateProps> = ({
  message,
  onRetry,
  retryLabel = "Retry",
  autoRetrySeconds,
}) => {
  const [countdown, setCountdown] = useState(autoRetrySeconds ?? 0);
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;

  // One-shot auto-retry: fires once per mount, doesn't re-arm on its own if
  // the retry also fails (avoids a tight retry loop against a still-down
  // service). The visible countdown gives the user a reason to wait rather
  // than immediately mashing the manual button.
  useEffect(() => {
    if (!autoRetrySeconds) return;
    setCountdown(autoRetrySeconds);
    const tick = setInterval(() => {
      setCountdown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    const fire = setTimeout(() => onRetryRef.current(), autoRetrySeconds * 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(fire);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRetrySeconds]);

  return (
    <div className="fetch-error-state" role="alert">
      <AlertCircle size={32} strokeWidth={1.5} className="fetch-error-state__icon" />
      <p className="fetch-error-state__message">{message}</p>
      {!!autoRetrySeconds && countdown > 0 && (
        <p className="fetch-error-state__countdown">Retrying in {countdown}s…</p>
      )}
      <button type="button" className="fetch-error-state__retry-btn" onClick={onRetry}>
        <RefreshCw size={14} />
        {retryLabel}
      </button>
    </div>
  );
};

export default FetchErrorState;
